import { FunctionComponent, useState } from "react"
import { Dropdown } from "react-daisyui"
import { useNavigate } from "react-router-dom"
import { useTypedDispatch, useTypedSelector } from "../app/store"
import { logoutUser } from "../features/auth/authThunks"
import HelpModal from "./help-modal"
import Breadcrumbs from "./breadcrumb/breadcrumbs"
import { Role } from "../features/auth/types"
import { setRole } from "../features/auth/authSlice"
import { useRoleRedirect } from "../features/auth/hooks/useRoleRedirect"
import { useOrganizationRecovery } from "../hooks/useOrganizationRecovery"

const RoleNameMap = {
  4: "Team Admin",
  5: "Simulation User",
  8: "Admin",
} as const

type RoleKey = keyof typeof RoleNameMap

type HeaderSectionProps = {
  hideBreadcrumbs?: boolean
  hideHelpIcon?: boolean
  hideProfileIcon?: boolean
}

const HeaderSection: FunctionComponent<HeaderSectionProps> = ({
  hideBreadcrumbs = false,
  hideHelpIcon = false,
  hideProfileIcon = false,
}) => {
  const dispatch = useTypedDispatch()
  const navigate = useNavigate()
  const { handleRoleRedirectForRole } = useRoleRedirect()

  // Use organization recovery hook to ensure organization ID is available
  useOrganizationRecovery()

  const {
    user,
    role: currentRole,
    organization,
  } = useTypedSelector((state) => state.authSlice)

  const [isHelpModalOpen, setHelpModalOpen] = useState(false)
  const [isDevMenuOpen, setIsDevMenuOpen] = useState(false)

  const handleLogout = () => {
    console.debug("Logging out")
    dispatch(logoutUser())
    navigate("/")
  }

  const handleRoleChange = (role: Role) => {
    dispatch(setRole(role))
    handleRoleRedirectForRole(role)
  }

  const toggleDevMenu = () => {
    setIsDevMenuOpen(!isDevMenuOpen)
  }

  return (
    <div className="relative bg-base-white box-border w-[100%] h-16 flex flex-row py-3 px-0 items-center justify-between text-left text-sm text-gray-600 font-text-md-regular1 border-b-[1px] border-solid border-stroke-light">
      <div className="flex-1 flex flex-row py-0 px-8 items-start justify-between">
        {!hideBreadcrumbs && <Breadcrumbs />}
        {hideBreadcrumbs && <div />}
        <div className="flex flex-row items-center justify-start gap-[16px]">
          {process.env.NODE_ENV === "development" && (
            <div className="flex flex-row items-start justify-start gap-[4px]">
              <div className="rounded-md bg-base-white overflow-hidden flex flex-row p-2.5 items-start justify-start relative">
                <button
                  className="btn btn-circle btn-ghost btn-sm bg-indigo-100 text-indigo-600 flex items-center justify-center relative z-10"
                  title="Dev Info"
                  onClick={toggleDevMenu}>
                  <span className="text-xs font-semibold">DEV</span>
                </button>

                {isDevMenuOpen && (
                  <div className="fixed top-16 right-8 mt-2 w-80 z-[9999] bg-white shadow-lg p-4 text-sm rounded-md border border-gray-200">
                    <div className="border-b pb-2 mb-2">
                      <div className="font-bold text-gray-800 text-base">
                        User Info
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="text-gray-500">ID:</div>
                        <div className="font-mono">{user.id}</div>
                        <div className="text-gray-500">Name:</div>
                        <div>
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-gray-500">Email:</div>
                        <div className="truncate">{user.email}</div>
                        <div className="text-gray-500">Organization:</div>
                        <div className="truncate">
                          {user.organizationName} (User ID:{" "}
                          {user.organizationId}, Store ID: {organization?.id})
                        </div>
                      </div>
                    </div>

                    <div className="border-b pb-2 mb-2">
                      <div className="font-bold text-gray-800">Role Info</div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="text-gray-500">Current Role:</div>
                        <div>
                          {user.roleName} (ID: {user.role})
                        </div>
                        <div className="text-gray-500">Available Roles:</div>
                        <div className="flex flex-wrap gap-1">
                          {user.roles?.map((role) => (
                            <span
                              key={role.roleId}
                              className={`px-1 rounded text-xs ${
                                role.roleId === currentRole.roleId
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100"
                              }`}>
                              {role.name || RoleNameMap[role.roleId as RoleKey]}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="font-bold text-gray-800">Auth State</div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="text-gray-500">Auth Status:</div>
                        <div className="flex items-center">
                          <span
                            className={`w-2 h-2 rounded-full mr-1 ${
                              user.status === 1 ? "bg-green-500" : "bg-red-500"
                            }`}></span>
                          {user.status === 1 ? "Active" : "Inactive"}
                        </div>
                        <div className="text-gray-500">Token:</div>
                        <div className="truncate font-mono text-xs">
                          {user.token?.substring(0, 16)}...
                        </div>
                        <div className="text-gray-500">Created:</div>
                        <div>{new Date(user.createdOn).toLocaleString()}</div>
                      </div>
                    </div>

                    <div className="border-t pt-2 mt-2">
                      <div className="font-bold text-gray-800">
                        Session Info
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="text-gray-500">Last Refresh:</div>
                        <div className="font-mono text-xs">
                          {localStorage.getItem("session_last_refresh")
                            ? new Date(
                                parseInt(
                                  localStorage.getItem(
                                    "session_last_refresh",
                                  ) || "0",
                                  10,
                                ),
                              ).toLocaleString()
                            : "Never"}
                        </div>
                        <div className="text-gray-500">Session Cookie:</div>
                        <div className="font-mono text-xs">
                          {document.cookie.includes("session-id")
                            ? "Present"
                            : "Missing"}
                        </div>
                        <div className="text-gray-500">Refresh Cookie:</div>
                        <div className="font-mono text-xs">
                          {document.cookie.includes("refresh-token")
                            ? "Present"
                            : "(HTTPOnly - Not Visible)"}
                        </div>
                        <div className="text-gray-500">All Cookies:</div>
                        <div
                          className="truncate font-mono text-xs cursor-pointer hover:text-primary-600"
                          onClick={() =>
                            console.log("Document cookies:", document.cookie)
                          }>
                          {document.cookie || "(None visible)"}
                        </div>
                        <div className="text-gray-500 col-span-2 mt-2">
                          <button
                            className="w-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 py-1 px-2 rounded text-xs"
                            onClick={async () => {
                              try {
                                const customWindow =
                                  window as unknown as Window & {
                                    meseeknaSession?: {
                                      refresh: () => Promise<unknown>
                                    }
                                  }

                                if (
                                  typeof window !== "undefined" &&
                                  customWindow.meseeknaSession
                                ) {
                                  const result =
                                    await customWindow.meseeknaSession.refresh()
                                  console.log("Manual refresh result:", result)
                                  // Force refresh the component
                                  setTimeout(
                                    () => setIsDevMenuOpen(false),
                                    1000,
                                  )
                                  setTimeout(() => setIsDevMenuOpen(true), 1100)
                                }
                              } catch (err) {
                                console.error("Failed to refresh session:", err)
                              }
                            }}>
                            Refresh Session Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {!hideHelpIcon && (
            <div className="flex flex-row items-start justify-start gap-[4px]">
              <div className="rounded-md bg-base-white overflow-hidden flex flex-row p-2.5 items-start justify-start">
                <button
                  className="btn btn-circle btn-ghost btn-sm w-5 h-5"
                  onClick={() => setHelpModalOpen(true)}>
                  <img
                    className="flex-1 rounded-15xl max-h-full w-6 object-cover cursor-pointer"
                    alt="Get Support"
                    src="/v3/assets/help-circle.svg"
                  />
                </button>
              </div>
            </div>
          )}
          {!hideProfileIcon && (
            <div className="flex flex-col items-start justify-start">
              <Dropdown dataTheme="light" end={true}>
                <Dropdown.Toggle button={false}>
                  <img
                    className="flex-1 rounded-15xl max-h-full w-10 object-cover cursor-pointer"
                    alt=""
                    src="/v3/assets/avatar2@2x.png"
                  />
                </Dropdown.Toggle>

                <Dropdown.Menu className="w-52 z-50">
                  <Dropdown.Item
                    role="menuItem fakeMenuItem"
                    className="hover:bg-transparent hover:cursor-default font-bold">
                    {user.firstName} {user.lastName}
                  </Dropdown.Item>

                  {process.env.NODE_ENV === "development" &&
                    user.roles?.length > 1 && (
                      <Dropdown.Item
                        role="menuItem fakeMenuItem"
                        className="hover:bg-transparent hover:cursor-default pb-1">
                        Change Role:
                      </Dropdown.Item>
                    )}

                  {user.roles &&
                    user.roles.map((role) => (
                      <Dropdown.Item
                        onClick={() => handleRoleChange(role)}
                        key={role.roleId}
                        className={`text-sm py-1 ml-2 ${
                          role.roleId === currentRole.roleId
                            ? "font-bold disabled"
                            : ""
                        }`}>
                        {RoleNameMap[role.roleId as RoleKey]}
                      </Dropdown.Item>
                    ))}

                  <Dropdown.Item
                    onClick={() => navigate("/account?showPasswordForm=true")}>
                    Change Password
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </div>
      </div>
      <HelpModal
        isHelpModalOpen={isHelpModalOpen}
        setHelpModalOpen={setHelpModalOpen}
        modalTitle="Meseekna Support"
      />
    </div>
  )
}
export default HeaderSection
